import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Send, Flag, Shield, AlertTriangle, Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../lib/authFetch';
import { UserAvatar } from '../lib/avatar';
import PageHeader from '../components/PageHeader';
import useTheme4A from '../components/useTheme4A';

/**
 * Direct Messages page.
 *
 * Two views:
 *   - `/dm`                    → thread list (left column on desktop, full-screen on mobile)
 *   - `/dm/:otherUserId`       → single thread view
 *
 * Safety UI:
 *   - Persistent info banner explaining SuperAdmin visibility.
 *   - Sobriety-gap amber banner when backend reports `gap_warning`.
 *   - Flag button on every message (own + other).
 *   - Auto-marks thread read on mount and every poll.
 */
function DirectMessagesPage() {
  useTheme4A();
  const { otherUserId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [threads, setThreads] = useState([]);
  const [threadDetail, setThreadDetail] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listEndRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchThreads = async () => {
    try {
      const res = await authFetch(`${backendUrl}/api/dm/threads`);
      if (res.ok) setThreads((await res.json()).items || []);
    } catch (e) { /* ignore */ }
  };

  const fetchThread = async () => {
    if (!otherUserId) return;
    try {
      const res = await authFetch(`${backendUrl}/api/dm/threads/${otherUserId}/messages`);
      if (res.ok) {
        const data = await res.json();
        setThreadDetail(data);
        // Mark read after successful fetch
        authFetch(`${backendUrl}/api/dm/threads/${otherUserId}/mark-read`, { method: 'POST' }).catch(() => {});
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not load thread');
      }
    } catch (e) { /* ignore */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: fetch thread list once; fetchThreads intentionally not a dep
  useEffect(() => { fetchThreads(); }, []);
  useEffect(() => {
    if (!otherUserId) { setThreadDetail(null); return; }
    fetchThread();
    // Poll thread every 8s while open — keeps unread counts + new messages fresh.
    const t = setInterval(fetchThread, 8000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when otherUserId changes; poll restarts per open thread, fetchThread intentionally excluded
  }, [otherUserId]);

  // Auto-scroll to newest message when it arrives.
  useEffect(() => {
    if (threadDetail && listEndRef.current) listEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [threadDetail]);

  const send = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const res = await authFetch(`${backendUrl}/api/dm/threads/${otherUserId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Send failed (HTTP ${res.status})`);
        return;
      }
      setText('');
      fetchThread();
      fetchThreads();
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  const flagMessage = async (msgId) => {
    if (!window.confirm('Report this message to SuperAdmin for review?')) return;
    try {
      const res = await authFetch(`${backendUrl}/api/dm/messages/${msgId}/flag`, { method: 'POST' });
      if (res.ok) {
        toast.success('Reported: thanks. SuperAdmin will review.');
        fetchThread();
      }
    } catch (e) { /* ignore */ }
  };

  // Compute derived messages array up front so hooks stay in the same order
  // across the two render branches (Rules of Hooks).
  const messages = useMemo(() => {
    return [...(threadDetail?.messages || [])].reverse();
  }, [threadDetail]);

  // Render: thread list view
  if (!otherUserId) {
    return (
      <div className="min-h-screen bg-zinc-100">
        <PageHeader title="Direct Messages" backTo="/recovery-room" />
        <main className="pt-20 pb-6 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-900 flex items-start gap-2" data-testid="dm-info-banner">
              <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p><strong>SuperAdmin can review any DM for safety.</strong> DMs are private
                between two mutual favourites: but not end-to-end encrypted. Use the flag
                icon on any message that feels wrong.</p>
              </div>
            </div>
            {threads.length === 0 ? (
              <div className="bg-white border border-zinc-200 rounded-lg p-6 text-center text-sm text-zinc-600" data-testid="dm-empty">
                <p>No DMs yet.</p>
                <p className="mt-1">Add a Favourite Friend in the Recovery Room to start a private thread.</p>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="dm-thread-list">
                {threads.map(t => (
                  <li
                    key={t.thread_key}
                    className="bg-white border border-zinc-200 rounded-lg p-3 hover:border-emerald-400 cursor-pointer"
                    onClick={() => navigate(`/dm/${t.other_user_id}`)}
                    data-testid={`dm-thread-${t.other_user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                        <Heart className="w-4 h-4 text-emerald-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-bold text-zinc-900 truncate">{t.other_handle}</span>
                          {t.unread_count > 0 && (
                            <span className="text-[10px] font-bold bg-emerald-600 text-white rounded-full px-2 py-0.5 flex-shrink-0" data-testid={`dm-unread-${t.other_user_id}`}>
                              {t.unread_count}
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 truncate ${t.last_message.flagged ? 'text-rose-600' : 'text-zinc-500'}`}>
                          {t.last_message.sender_id === user?.user_id ? 'You: ' : ''}
                          {t.last_message.content || '(empty)'}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Single thread view
  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col">
      <PageHeader
        title={threadDetail?.other_handle || 'Direct Message'}
        backTo="/dm"
      />
      <main className="pt-16 pb-24 flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-900 flex items-center gap-1.5 mb-3">
            <Shield className="w-3 h-3 flex-shrink-0" />
            <span>SuperAdmin can review any DM. Flag anything that feels wrong.</span>
          </div>

          {threadDetail?.gap_warning && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="dm-gap-warning">
              <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900">
                <p className="font-bold mb-1">Sobriety-time gap</p>
                <p>{threadDetail.gap_warning}</p>
              </div>
            </div>
          )}

          <ul className="space-y-2" data-testid="dm-message-list">
            {messages.length === 0 && (
              <li className="text-center text-sm text-zinc-500 py-6">No messages yet. Say hello 👋</li>
            )}
            {messages.map(m => {
              const isOwn = m.sender_id === user?.user_id;
              return (
                <li
                  key={m.message_id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  data-testid={`dm-message-${m.message_id}`}
                >
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                    isOwn ? 'bg-emerald-600 text-white' : 'bg-white border border-zinc-200 text-zinc-900'
                  } ${m.flagged ? 'ring-2 ring-rose-400' : ''}`}>
                    <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>
                    <div className={`flex items-center gap-2 mt-1 ${isOwn ? 'text-emerald-100' : 'text-zinc-400'}`}>
                      <span className="text-[10px]">
                        {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {m.flagged && (
                        <span className="text-[10px] font-bold text-rose-500 bg-rose-50 rounded px-1.5">Flagged</span>
                      )}
                      <button
                        onClick={() => flagMessage(m.message_id)}
                        className={`ml-auto hover:opacity-100 opacity-60 ${isOwn ? 'text-white' : 'text-zinc-500'}`}
                        title="Report this message"
                        data-testid={`dm-flag-${m.message_id}`}
                      >
                        <Flag className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
            <li ref={listEndRef} />
          </ul>
        </div>
      </main>

      {/* Composer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 p-3">
        <div className="max-w-2xl mx-auto flex gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a private message…"
            className="flex-1 border border-zinc-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-emerald-500"
            data-testid="dm-composer-input"
            maxLength={4000}
          />
          <button
            onClick={send}
            disabled={sending || !text.trim()}
            className="bg-emerald-600 text-white rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-50"
            data-testid="dm-send-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default DirectMessagesPage;
